import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../context/AuthContext.jsx";
import {
  fundById,
  computeEffectiveReturn,
  futureValueOfContributions,
  futureValueOfLumpSum,
} from "./data.js";
import Celebration from "../../components/Celebration.jsx";

const LEVEL_ID = "compound-quest";

function formatMoney(n) {
  return Math.round(n).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function computeOutcome({ reconResult, plan, campaignResult }) {
  const { log, totals } = campaignResult;
  const vehicle = fundById[plan.vehicleId];

  const effectiveReturn = computeEffectiveReturn({
    baseReturn: vehicle.netAnnualReturn,
    reinvestDividends: plan.reinvestDividends,
    taxAdvantaged: plan.accountType === "tax-advantaged",
    returnOverride: totals.returnOverride,
  });
  const effectiveMonthly = Math.max(0, plan.monthlyContribution + totals.contributionDelta);
  const effectiveYears = Math.max(1, plan.horizonYears - totals.yearsPenalty);
  const months = effectiveYears * 12;

  const contributionsFV = futureValueOfContributions(effectiveMonthly, effectiveReturn, months);
  const lumpSumFV = totals.lumpSum > 0 ? futureValueOfLumpSum(totals.lumpSum, effectiveReturn, effectiveYears / 2) : 0;
  const finalFV = Math.max(0, contributionsFV + lumpSumFV - totals.principalHit);

  // Optimal baseline: same starting contribution & horizon, ideal execution throughout.
  const optimalReturn = computeEffectiveReturn({
    baseReturn: fundById.index.netAnnualReturn,
    reinvestDividends: true,
    taxAdvantaged: true,
    returnOverride: null,
  });
  const optimalMonthly = plan.monthlyContribution + 50;
  const optimalMonths = plan.horizonYears * 12;
  const optimalFV =
    futureValueOfContributions(optimalMonthly, optimalReturn, optimalMonths) +
    futureValueOfLumpSum(2000, optimalReturn, plan.horizonYears / 2);

  const pctOfPotential = Math.min(100, Math.round((finalFV / optimalFV) * 100));

  const goodChoices = log.filter((l) => l.choice.tag === "good").length;
  const badChoices = log.filter((l) => l.choice.tag === "bad").length;

  const reconScore = reconResult.correctCount * 15;
  const choiceScore = goodChoices * 20 - badChoices * 20;
  const potentialBonus = Math.round(pctOfPotential * 2);
  const planBonus =
    (plan.accountType === "tax-advantaged" ? 50 : 0) +
    (plan.reinvestDividends ? 50 : 0) +
    (plan.vehicleId === "index" ? 50 : 0);

  const score = Math.max(50, reconScore + choiceScore + potentialBonus + planBonus);

  return { finalFV, optimalFV, pctOfPotential, effectiveReturn, effectiveYears, effectiveMonthly, goodChoices, badChoices, score };
}

export default function DebriefStage({ reconResult, plan, campaignResult, onRetry }) {
  const navigate = useNavigate();
  const { user, profile, refreshProfile } = useAuth();
  const [saving, setSaving] = useState(true);
  const [saved, setSaved] = useState(false);

  const outcome = computeOutcome({ reconResult, plan, campaignResult });
  const didLevelUp = Math.floor(((profile?.xp ?? 0) + outcome.score) / 500) + 1 > (profile?.level ?? 1);

  useEffect(() => {
    let cancelled = false;

    async function save() {
      if (!user) {
        setSaving(false);
        return;
      }
      const newXp = (profile?.xp ?? 0) + outcome.score;
      const newLevel = Math.max(profile?.level ?? 1, Math.floor(newXp / 500) + 1);
      const newNetWorth = (profile?.net_worth_cents ?? 0) + outcome.score * 100;

      const { error } = await supabase
        .from("profiles")
        .update({ xp: newXp, level: newLevel, net_worth_cents: newNetWorth })
        .eq("id", user.id);

      if (!cancelled) {
        if (!error) {
          await refreshProfile();
          const { error: challengeError } = await supabase.rpc("record_level_result", {
            p_level_id: LEVEL_ID,
            p_score: outcome.score,
          });
          if (challengeError) {
            console.error("Failed to record challenge result:", challengeError.message);
          }
          setSaved(true);
        } else {
          console.error("Failed to save Compound Quest results:", error.message);
        }
        setSaving(false);
      }
    }

    save();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="animate-popIn text-center">
      <Celebration show={saved} levelUp={didLevelUp} />
      <h2 className="pixel-heading mb-1 text-lg text-lime">QUEST DEBRIEF</h2>
      <p className="mb-6 text-xs uppercase tracking-widest text-mist/50">
        {plan.horizonYears}-Year Journey Complete
      </p>

      <div className="mx-auto mb-4 max-w-sm border-2 border-gold bg-gold/10 p-5">
        <p className="label-pixel mb-1 text-gold">Projected Nest Egg</p>
        <p className="font-display text-2xl text-gold">{formatMoney(outcome.finalFV)}</p>
        <p className="mt-2 text-[11px] text-mist/60">
          From {formatMoney(outcome.effectiveMonthly)}/mo over ~{outcome.effectiveYears} years at
          roughly {outcome.effectiveReturn.toFixed(1)}%/yr, compounded.
        </p>
      </div>

      <div className="mx-auto mb-6 max-w-sm space-y-2 border-2 border-black bg-ink p-4 text-left text-sm">
        <div className="flex justify-between">
          <span className="text-mist/60">Recon Accuracy</span>
          <span className="text-cyan">{reconResult.correctCount}/5</span>
        </div>
        <div className="flex justify-between">
          <span className="text-mist/60">Good Calls / Rough Calls</span>
          <span>
            <span className="text-lime">{outcome.goodChoices}</span>
            <span className="text-mist/40"> / </span>
            <span className="text-danger">{outcome.badChoices}</span>
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-mist/60">% of Best-Case Outcome</span>
          <span className={outcome.pctOfPotential >= 80 ? "text-lime" : outcome.pctOfPotential >= 50 ? "text-gold" : "text-danger"}>
            {outcome.pctOfPotential}%
          </span>
        </div>
        <div className="flex justify-between border-t border-black/50 pt-2 text-[11px] text-mist/50">
          <span>Best-case with the same monthly commitment</span>
          <span>{formatMoney(outcome.optimalFV)}</span>
        </div>
        <div className="relative flex justify-between border-t border-black/50 pt-2 font-display text-[11px]">
          <span className="text-mist/60">SCORE</span>
          <span className="text-gold">{outcome.score.toLocaleString()}</span>
          {saved && (
            <span className="absolute -top-4 right-0 animate-floatUp text-[9px] text-lime">+{outcome.score} XP</span>
          )}
        </div>
      </div>

      <p className="mx-auto mb-6 max-w-sm text-[11px] text-mist/50">
        Same starting commitment, different outcome — the gap between your result and the
        best-case number is entirely decision quality: which vehicle, whether you held through
        the dips, and whether you let fees and hype eat into the compounding.
      </p>

      <p className="mb-6 text-xs text-mist/50">
        {saving ? "Saving to your profile…" : saved ? "Net worth and XP updated on your profile ✅" : "Results ready."}
      </p>

      <div className="flex flex-wrap justify-center gap-3">
        <button onClick={onRetry} className="btn-magenta">
          ↻ RUN A NEW QUEST
        </button>
        <button onClick={() => navigate("/")} className="btn-cyan">
          LEVEL SELECT
        </button>
      </div>
    </div>
  );
}
